export const enRoles = {
  title: "Roles",
  subtitle: "View all roles and the permissions assigned to them.",
  empty: "No roles found.",
  actions: {
    newRole: "New Role",
    editRoleAria: "Edit role {{name}}",
  },
  newRoleModal: {
    title: "New Role",
    editTitle: "Edit Role",
    closeAria: "Close dialog",
    fields: {
      name: "Role name",
      scope: "Assigned at",
      scopeOptions: {
        domain: "A domain",
        tenant: "The whole workspace",
      },
      scopeHelp: {
        domain: "Granted per domain, from that domain's Access tab. Applies to that domain only.",
        tenant:
          "Granted on the Users screen. Workspace settings, plus any domain permission it holds " +
          "applies to every domain.",
      },
      scopeLockedHelp: "A role's level cannot be changed after it is created.",
      permissions: "Permissions",
      selectAll: "Select All",
    },
    permissionGroups: {
      atRiskTitle_one: "This role has 1 permission that no group covers",
      atRiskTitle_other: "This role has {{count}} permissions that no group cover",
      atRiskBody:
        "Saving replaces this role's permissions with the groups ticked below, so these will be " +
        "removed. Tick the group that contains them to keep them.",
      areas: {
        chats: "Chats",
        apps: "Apps",
        "app-studio": "App Studio",
        domains: "Data Hub",
        "domain-access": "Domain access",
        nuggets: "Nuggets",
        actions: "Actions Hub",
        users: "Users",
        roles: "Roles",
        "workspace-access": "Workspace access",
        settings: "Settings",
      },
      groups: {
        "chats.use": {
          label: "Chat with your data",
          description: "Start, rename and delete chats, and ask questions of any domain.",
        },
        "chats.trace": {
          label: "See how an answer was generated",
          description:
            "Open the trace behind a chat answer, including the prompts sent to the model.",
        },
        "apps.view": {
          label: "View published apps",
          description: "Open published apps and read every widget in them.",
        },
        "apps.approve": {
          label: "Approve rows in table widgets",
          description:
            "Approve selections and set classifications inside a published approval table.",
        },
        "app-studio.all": {
          label: "Use App Studio",
          description:
            "Open App Studio and build apps: create, edit and delete apps and every widget in " +
            "them. There is no view-only option.",
        },
        "domains.view": {
          label: "View domains and data model",
          description: "Open the Data Hub and read a domain's overview and semantic model.",
        },
        "domains.edit": {
          label: "Edit domains and data model",
          description:
            "Curate the semantic model, publish changes, and edit a domain's overview. Some " +
            "actions here spend LLM budget.",
        },
        "domains.documents": {
          label: "Manage documents",
          description:
            "Open a domain's Documents tab, and upload and delete its documents. There is no " +
            "view-only option.",
        },
        "domains.alation": {
          label: "Manage the Alation catalog connection",
          description:
            "Open a domain's Alation tab, connect or update the service account, and run "
            + "Sync with Alation to pull catalog metadata into the semantic model.",
        },
        "domains.setup": {
          label: "Set up domains and connections",
          description:
            "Create domains and add warehouse connections. Includes reading connection " +
            "credentials.",
        },
        "domains.nugget-configs": {
          label: "Manage nugget configs",
          description:
            "Open a domain's Nugget Configs tab and create, edit and delete the configs that " +
            "generate nuggets on a schedule, and run them on demand. There is no view-only option.",
        },
        "domains.jobs": {
          label: "Manage jobs",
          description:
            "Open a domain's Jobs tab and create, run and delete scheduled jobs. Jobs are " +
            "workspace-wide, so this covers every domain. There is no view-only option.",
        },
        "domain-access.view": {
          label: "See who has access to a domain",
          description: "Read the Access tab on a domain. Also opens the Users and Roles lists.",
        },
        "domain-access.manage": {
          label: "Grant and revoke domain access",
          description: "Give and take away roles on a domain, from that domain's Access tab.",
        },
        "nuggets.view": {
          label: "View nuggets",
          description: "Read nuggets and leave feedback on them.",
        },
        "nuggets.edit": {
          label: "Edit nuggets",
          description: "Change authored fields on a nugget, such as its severity.",
        },
        "actions.nugget-actions": {
          label: "Take and track nugget actions",
          description:
            "Run a recommended action from a nugget and follow it on the Actions Hub page.",
        },
        "actions.integrations": {
          label: "Manage integrations",
          description: "Read and connect the integrations that nugget actions are delivered to.",
        },
        "users.view": {
          label: "View users",
          description: "Open the Users page and see everyone in the workspace, read-only.",
        },
        "users.manage": {
          label: "Manage users",
          description: "Invite, edit and remove users, and clear a user's cached responses.",
        },
        "roles.view": {
          label: "View roles",
          description: "Open the Roles page and read every role and its permissions.",
        },
        "roles.manage": {
          label: "Create and edit roles",
          description:
            "Add and change roles. You can only grant permissions you already hold yourself.",
        },
        "workspace-access.view": {
          label: "View workspace role assignments",
          description: "See which workspace-wide roles each user holds.",
        },
        "workspace-access.manage": {
          label: "Assign and revoke workspace roles",
          description:
            "Give and take away workspace-wide roles. You can only assign roles whose " +
            "permissions you already hold.",
        },
        "settings.business-context-view": {
          label: "View business context",
          description: "Read the workspace's business context in Settings.",
        },
        "settings.business-context-edit": {
          label: "Edit business context",
          description: "Change the business context that guides how answers are generated.",
        },
      },
    },
    validation: {
      required: "This field is required.",
    },
    actions: {
      cancel: "Cancel",
      addRole: "Add Role",
      saveRole: "Save Changes",
    },
    errors: {
      saveFailed: "Failed to add role. Please try again.",
      updateFailed: "Failed to update role. Please try again.",
    },
  },
  table: {
    name: "Name",
    scope: "Assigned at",
    scopeLabel: {
      domain: "A domain",
      tenant: "The whole workspace",
    },
    permissions: "Permissions",
    permissionCount: "{{count}} permissions",
    createdAt: "Created",
    ariaLabel: "Roles",
  },
} as const;
